import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Typography from '../../components/Typography';
import Button from '../../components/Button/Button';
import Modal from '../../components/Modal/Modal';
import AddVehicleForm from '../../components/Forms/AddVehicleForm';
import VehicleCard from '../../components/Card/VehicleCard';
import { useVehicles } from '../../context/VehicleContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import GarageOutlineIcon from '../../components/Icons/GarageOutlineIcon';
import AddCircleSolidIcon from '../../components/Icons/AddCircleSolidIcon';

const GarageStep = ({ onComplete }) => {
  const { t } = useTranslation('completeProfile');
  const { user } = useAuth();
  const { vehicles, addVehicle, updateVehicle, deleteVehicle, fetchVehicles } = useVehicles();
  const navigate = useNavigate();
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false);
  const [showEditVehicleForm, setShowEditVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  // Cargar vehículos cuando se monta el componente
  useEffect(() => {
    if (user && (user.id || user._id)) {
      fetchVehicles(user.id || user._id);
    }
  }, [fetchVehicles, user]);

  const handleAddVehicle = () => {
    setShowAddVehicleForm(true);
  };

  const handleVehicleAdded = () => {
    setShowAddVehicleForm(false);
  };

  const handleCancelAddVehicle = () => {
    setShowAddVehicleForm(false);
  };

  const handleSubmit = async (vehicleData) => {
    const createdVehicle = await addVehicle(vehicleData);
    if (createdVehicle) {
      handleVehicleAdded();
    }
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowEditVehicleForm(true);
  };

  const handleDeleteVehicle = (vehicleId) => {
    setVehicleToDelete(vehicleId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteVehicle = async () => {
    if (vehicleToDelete) {
      const result = await deleteVehicle(vehicleToDelete);
      // Evitar cualquier redirección automática
      if (result) {
        navigate('/complete-profile');
      }
      setShowDeleteConfirmation(false);
      setVehicleToDelete(null);
    }
  };

  const cancelDeleteVehicle = () => {
    setShowDeleteConfirmation(false);
    setVehicleToDelete(null);
  };

  const handleEditSubmit = async (vehicleData) => {
    const updatedVehicle = await updateVehicle(editingVehicle._id, vehicleData);
    if (updatedVehicle) {
      setShowEditVehicleForm(false);
      setEditingVehicle(null);
    }
  };

  const handleCancelEdit = () => {
    setShowEditVehicleForm(false);
    setEditingVehicle(null);
  };

  return (
    <GarageContainer>
      {/* Modal para añadir vehículo */}
      {showAddVehicleForm && (
        <Modal
          title={t('profile.addVehicle')}
          onClose={handleCancelAddVehicle}
          maxWidth="600px"
        >
          <AddVehicleForm
            onSubmit={handleSubmit}
            onClose={handleCancelAddVehicle}
          />
        </Modal>
      )}

      {/* Modal para editar vehículo */}
      {showEditVehicleForm && editingVehicle && (
        <Modal
          title={t('garage.editVehicle.title')}
          onClose={handleCancelEdit}
          maxWidth="600px"
        >
          <AddVehicleForm
            vehicle={editingVehicle}
            onSubmit={handleEditSubmit}
            onClose={handleCancelEdit}
          />
        </Modal>
      )}

      {/* Modal de confirmación para eliminar vehículo */}
      {showDeleteConfirmation && (
        <Modal
          title={t('garage.deleteVehicle.title')}
          onClose={cancelDeleteVehicle}
          maxWidth="400px"
        >
          <DeleteConfirmationContent>
            <Typography $variant="body-1-regular" color={theme.colors.defaultMain}>
              {/* ¿Estás seguro de que quieres eliminar este vehículo? Esta acción no se puede deshacer. */}
              {t('garage.deleteVehicle.confirmation')}
            </Typography>
            <DeleteConfirmationActions>
              <Button type="button" $variant="outline" size="l" onClick={cancelDeleteVehicle}>
                {/* Cancelar */}
                {t('garage.deleteVehicle.cancel')}
              </Button>
              <Button type="button" $variant="danger" size="l" onClick={confirmDeleteVehicle}>
                {/* Eliminar */}
                {t('garage.deleteVehicle.delete')}
              </Button>
            </DeleteConfirmationActions>
          </DeleteConfirmationContent>
        </Modal>
      )}

      {/* Introducción */}
      <IntroSection>
        <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong}>
          {/* Sube tus vehículos y presume de garaje */}
          {t('garage.title')}
        </Typography>
        <Typography $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
          {/* Guarda tus motos o coches ahora para inscribirte más rápido en los eventos y que otros puedan ver tus máquinas en tu perfil. */}
          {t('garage.subtitle')}
        </Typography>
      </IntroSection>

      {/* Lista de vehículos o estado vacío */}
      {vehicles && vehicles.length > 0 ? (
        <VehiclesSection>
          <VehiclesList>
            {vehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle._id || vehicle.id}
                vehicle={vehicle}
                isOwnGarage={true}
                originContext="complete-profile"
                openEditModal={openEditModal}
                handleDeleteVehicle={handleDeleteVehicle}
              />
            ))}
            <AddAnotherVehicleSection>
              <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak} $align="center">
                {/* ¿Tienes más vehículos? Añádelos a tu garaje */}
                {t('garage.addMoreVehicles.title')}
              </Typography>
              <Button type="button" $variant="outline" size="l" onClick={handleAddVehicle}>
                <AddCircleSolidIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                <Typography as="span" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                  {/* Añadir otro vehículo */}
                  {t('garage.addMoreVehicles.action')}
                </Typography>
              </Button>
            </AddAnotherVehicleSection>
          </VehiclesList>
        </VehiclesSection>
      ) : (
        <EmptyStateSection>
          <EmptyStateContent>
            <GarageOutlineIcon fill={theme.colors.defaultSubtle} width={24} height={24} />
            <Typography $variant="body-1-semibold" $align="center" color={theme.colors.defaultStrong}>
              {/* Tu garaje está vacío */}
              {t('garage.emptyGarage.title')}
            </Typography>
            <Typography $variant="body-2-regular" $align="center" color={theme.colors.defaultWeak}>
              {/* Añade tu primer vehículo para empezar a construir tu garaje */}
              {t('garage.emptyGarage.subtitle')}
            </Typography>
          </EmptyStateContent>

          <EmptyStateActions>
            <Button type="button" $variant="defaultMain" size="l" onClick={handleAddVehicle}>
              <AddCircleSolidIcon fill={theme.colors.inverseMain} width={16} height={16} />
              {/* Añadir vehículo */}
              <Typography as="span" $variant="body-1-semibold" color={theme.colors.inverseMain}>
                {/* Añadir vehículo */}
                {t('garage.emptyGarage.mainAction')}
              </Typography>
            </Button>
          </EmptyStateActions>
        </EmptyStateSection>
      )}
    </GarageContainer>
  );
};

GarageStep.propTypes = {
  onComplete: PropTypes.func
};

export default GarageStep;

// Styled Components
const GarageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const IntroSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.md};
  text-align: center;
`;

const VehiclesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const VehiclesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const AddAnotherVehicleSection = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.sm};
  grid-column: span 1;
  padding: ${theme.sizing.md};
  text-align: center;
`;

const DeleteConfirmationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const DeleteConfirmationActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: flex-end;
`;

const EmptyStateSection = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md};
  text-align: center;
`;

const EmptyStateContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  max-width: 300px;
`;

const EmptyStateActions = styled.div`
  display: flex;
  gap: 12px;
`;
